import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth, API_ENDPOINTS } from '../config/api';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Purchase {
  type: 'purchase';
  credits: number;
  amount_total: number | null;   // cents
  currency: string;
  session_id: string;
  created_at: string | null;
}

interface Usage {
  type: 'usage';
  credits: number;               // always -1
  filename: string;
  doc_id: string;
  created_at: string | null;
}

type Transaction = Purchase | Usage;

interface History {
  balance: number;
  total_purchased: number;
  total_used: number;
  transactions: Transaction[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso: string | null) {
  if (!iso) return 'Date unknown';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtAmount(cents: number | null, currency: string) {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({
  label, value, sub, accent,
}: { label: string; value: string | number; sub?: string; accent?: boolean }) {
  return (
    <div className="theme-card p-6 flex flex-col gap-1">
      <p className="text-sm theme-text-secondary font-medium">{label}</p>
      <p className="text-4xl font-extrabold mt-1"
        style={{ color: accent ? 'var(--accent-primary)' : 'var(--text-primary)' }}>
        {value}
      </p>
      {sub && <p className="text-xs theme-text-secondary mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CreditsPage() {
  const { currentUser } = useAuth();
  const [history, setHistory] = useState<History | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser) { setLoading(false); return; }
    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetchWithAuth(API_ENDPOINTS.CREDIT_HISTORY, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHistory(await res.json());
      } catch {
        setError('Failed to load credit history.');
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser]);

  // Denominator = docs summarized + credits still in the tank.
  // This correctly accounts for free credits, manually added credits, etc.
  // that never appear in processed_sessions (Stripe purchases only).
  const totalEver = history ? history.total_used + history.balance : 0;
  const pct = totalEver > 0
    ? Math.round((history!.total_used / totalEver) * 100)
    : 0;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navigation />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-extrabold theme-text-primary">Credits</h1>
            <p className="text-sm theme-text-secondary mt-1">
              Your purchase and usage history
            </p>
          </div>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', boxShadow: '0 4px 14px rgba(79,70,229,0.35)' }}
          >
            + Buy more credits
          </Link>
        </div>

        {loading ? (
          <div className="space-y-4 animate-pulse">
            <div className="grid grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-28 rounded-2xl" style={{ background: 'var(--border-color)' }} />)}
            </div>
            <div className="h-64 rounded-2xl" style={{ background: 'var(--border-color)' }} />
          </div>
        ) : error ? (
          <div className="text-red-500 text-sm p-4 rounded-xl border border-red-200 bg-red-50">{error}</div>
        ) : !currentUser ? (
          <p className="theme-text-secondary">Please sign in to view your credits.</p>
        ) : history ? (
          <>
            {/* ── Stat cards ── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <StatCard
                label="Current Balance"
                value={history.balance}
                sub={history.balance === 1 ? 'credit remaining' : 'credits remaining'}
                accent
              />
              <StatCard
                label="Total Received"
                value={totalEver}
                sub={`${history.transactions.filter(t => t.type === 'purchase').length} Stripe purchase${history.transactions.filter(t => t.type === 'purchase').length !== 1 ? 's' : ''} + free credits`}
              />
              <StatCard
                label="Total Used"
                value={history.total_used}
                sub={`${history.total_used} PDF${history.total_used !== 1 ? 's' : ''} summarized`}
              />
            </div>

            {/* ── Usage bar ── */}
            {totalEver > 0 && (
              <div className="theme-card p-5 mb-6">
                <div className="flex justify-between text-sm mb-3">
                  <span className="font-medium theme-text-primary">Credit usage</span>
                  <span className="theme-text-secondary">
                    {history.total_used} of {totalEver} used ({pct}%)
                  </span>
                </div>
                <div className="h-3 rounded-full overflow-hidden" style={{ background: 'var(--bg-tertiary)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(pct, 100)}%`,
                      background: pct >= 90
                        ? 'linear-gradient(90deg,#f59e0b,#ef4444)'
                        : 'linear-gradient(90deg,#4f46e5,#7c3aed)',
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs theme-text-secondary mt-2">
                  <span>{history.total_used} used</span>
                  <span>{history.balance} remaining</span>
                </div>
              </div>
            )}

            {/* ── Transaction list ── */}
            <div className="theme-card overflow-hidden">
              <div className="px-6 py-4 border-b theme-border flex items-center justify-between">
                <h2 className="font-semibold theme-text-primary">Transaction history</h2>
                <span className="text-xs theme-text-secondary">
                  {history.transactions.length} event{history.transactions.length !== 1 ? 's' : ''}
                </span>
              </div>

              {history.transactions.length === 0 ? (
                <div className="flex flex-col items-center py-20 gap-3 text-center">
                  <span className="text-4xl">💳</span>
                  <p className="font-medium theme-text-primary">No transactions yet</p>
                  <p className="text-sm theme-text-secondary">
                    Your purchases and document summaries will appear here.
                  </p>
                  <Link to="/pricing" className="text-sm theme-link font-medium mt-1">
                    Buy your first credits →
                  </Link>
                </div>
              ) : (
                <ul className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                  {history.transactions.map((tx, i) => (
                    <li key={i} className="flex items-center gap-4 px-6 py-4 hover:bg-opacity-50 transition-colors"
                      style={{ background: 'var(--bg-secondary)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-secondary)')}
                    >
                      {/* Icon */}
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                        style={{
                          background: tx.type === 'purchase'
                            ? 'linear-gradient(135deg,#d1fae5,#a7f3d0)'
                            : 'linear-gradient(135deg,var(--accent-light),rgba(124,58,237,0.12))',
                        }}>
                        {tx.type === 'purchase' ? '💳' : '📄'}
                      </div>

                      {/* Description */}
                      <div className="flex-1 min-w-0">
                        {tx.type === 'purchase' ? (
                          <>
                            <p className="text-sm font-semibold theme-text-primary">
                              Purchased {tx.credits} credit{tx.credits !== 1 ? 's' : ''}
                            </p>
                            <p className="text-xs theme-text-secondary mt-0.5 truncate">
                              {fmtDate(tx.created_at)}
                              {tx.amount_total != null && ` · ${fmtAmount(tx.amount_total, tx.currency)}`}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-semibold theme-text-primary truncate">
                              {tx.filename}
                            </p>
                            <p className="text-xs theme-text-secondary mt-0.5">
                              {fmtDate(tx.created_at)} · PDF summarized
                            </p>
                          </>
                        )}
                      </div>

                      {/* Amount badge */}
                      <div className="flex-shrink-0 text-sm font-bold tabular-nums"
                        style={{ color: tx.type === 'purchase' ? '#10b981' : 'var(--text-secondary)' }}>
                        {tx.type === 'purchase' ? `+${tx.credits}` : '−1'}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
